import React from "react";
import { IconContainer, Icon } from "./styledComponents";
import { Filter as Option } from "../../types/PageFilterTypes";
import { FlagColor, FlagStatus } from "../../enums/Status";
import RedFlag from "../../assets/svg/flag_red.svg";
import GreenFlag from "../../assets/svg/flag_green.svg";
import YellowFlag from "../../assets/svg/flag_yellow.svg";
import WhiteFlag from "../../assets/svg/flag_white.svg";
import { FlagKey, TaskStatus } from "../../models/workflowModels";
import { MESSAGES } from "../../messages";

export const FLAG_COLOR_TO_STATUS: { [key: string]: FlagStatus } = {
  [FlagColor.RED]: FlagStatus.BLOCKED,
  [FlagColor.YELLOW]: FlagStatus.IN_PROGRESS,
  [FlagColor.GREEN]: FlagStatus.RESOLVED,
  [FlagColor.WHITE]: FlagStatus.DEFAULT,
};

export const TASK_STATUS_TO_FLAG_STATUS = {
  [TaskStatus.NOT_STARTED]: FlagStatus.DEFAULT,
  [TaskStatus.IN_PROGRESS]: FlagStatus.IN_PROGRESS,
  [TaskStatus.COMPLETED]: FlagStatus.RESOLVED,
  [TaskStatus.STUCK]: FlagStatus.BLOCKED,
};

export const FLAG_STATUS_TO_FLAG_KEY: { [key: string]: FlagKey } = {
  [FlagStatus.IN_PROGRESS]: FlagKey.IN_PROGRESS,
  [FlagStatus.RESOLVED]: FlagKey.RESOLVED,
  [FlagStatus.BLOCKED]: FlagKey.BLOCKED,
};

export const FLAG_KEY_TO_STATUS: { [key: string]: FlagStatus } = {
  [FlagKey.IN_PROGRESS]: FlagStatus.IN_PROGRESS,
  [FlagKey.RESOLVED]: FlagStatus.RESOLVED,
  [FlagKey.BLOCKED]: FlagStatus.BLOCKED,
};

export const STATUS_TO_FLAG_KEY: { [key: string]: FlagKey } = {
  [FlagStatus.IN_PROGRESS]: FlagKey.IN_PROGRESS,
  [FlagStatus.RESOLVED]: FlagKey.RESOLVED,
  [FlagStatus.BLOCKED]: FlagKey.BLOCKED,
};

export const FLAG_STATUS_TO_TASK_STATUS = {
  [FlagStatus.DEFAULT]: TaskStatus.NOT_STARTED,
  [FlagStatus.IN_PROGRESS]: TaskStatus.IN_PROGRESS,
  [FlagStatus.RESOLVED]: TaskStatus.COMPLETED,
  [FlagStatus.BLOCKED]: TaskStatus.STUCK,
};

export const FLAG_MAP: { [key: string]: string } = {
  [FlagColor.WHITE]: WhiteFlag,
  [FlagColor.YELLOW]: YellowFlag,
  [FlagColor.GREEN]: GreenFlag,
  [FlagColor.RED]: RedFlag,
};

export const buildFlagOptions = (flagKey?: FlagKey): Option[] => {
  const flagOptions: Option[] = [];

  // Derive the current flag color from the provided flag status, if any.
  const flagStatus: FlagStatus =
    FLAG_KEY_TO_STATUS[flagKey || MESSAGES.NOT_AVAILABLE] || FlagStatus.DEFAULT;
  const currentFlagColor = flagStatus
    ? Object.keys(FLAG_COLOR_TO_STATUS).find(
        (key) => FLAG_COLOR_TO_STATUS[key] === flagStatus
      )
    : MESSAGES.NOT_AVAILABLE;

  // Filter the flag colors based on the flag status.
  const validFlags = ((): FlagColor[] => {
    switch (flagStatus) {
      case FlagStatus.IN_PROGRESS:
        return [FlagColor.YELLOW, FlagColor.GREEN, FlagColor.RED];
      case FlagStatus.RESOLVED:
        return [FlagColor.GREEN, FlagColor.YELLOW];
      case FlagStatus.BLOCKED:
        return [FlagColor.RED, FlagColor.YELLOW, FlagColor.GREEN];
      default:
        return [FlagColor.WHITE, FlagColor.YELLOW];
    }
  })();

  // Ensure the current flag is the first option in the array.
  if (currentFlagColor) {
    flagOptions.push({
      key: currentFlagColor,
      displayValue: currentFlagColor,
      component: StatusFlag,
    });
  }

  // Add other valid flags, excluding the current flag if already added.
  validFlags.forEach((flagColor) => {
    if (flagColor !== currentFlagColor) {
      flagOptions.push({
        key: flagColor,
        displayValue: flagColor,
        component: StatusFlag,
      });
    }
  });

  return flagOptions;
};

export const StatusFlag = ({ dataIndex }: StatusFlagProps): JSX.Element => {
  return (
    <>
      <IconContainer p={1} m={1} size={20}>
        <Icon
          src={(dataIndex && FLAG_MAP[dataIndex]) || FLAG_MAP[FlagColor.WHITE]}
        />
      </IconContainer>
    </>
  );
};

type StatusFlagProps = {
  dataIndex?: string;
};
